// Core
import React, { useState } from 'react';

// Libraries
import { SwitchSize as SizeTypeAntD } from 'antd/es/switch';
import { useTheme } from 'styled-components';

// Components
import { Paragraph, ParagraphSize } from '../Paragraph';

// Component
import { SwitchProps, SwitchWithTextSize } from './Switch.types';
import {
  Container,
  Element,
  SupportingText,
  SupportingTextContainer,
  TextsContainer,
  TitleContainer,
} from './Switch.style';

// Types
import { Theme } from '../../../types/theme';

function Switch({
  checked = false,
  className,
  disabled = false,
  onChange,
  size = SwitchWithTextSize.md,
  supportingText,
  testId,
  text,
}: SwitchProps) {
  // Dependencies
  const theme: Theme = useTheme();

  // State for tracking hover status of the supporting text
  const [isSupportingTextHovered, setIsSupportingTextHovered] = useState<boolean>(false);

  /* **********************************************************************************************
  ******************************************* METHODS *********************************************
  ********************************************************************************************** */

  const convertSizeToAntD = (switchSize: SwitchWithTextSize): SizeTypeAntD => {
    const mapSize: Record<SwitchWithTextSize, SizeTypeAntD> = {
      [SwitchWithTextSize.sm]: 'small',
      [SwitchWithTextSize.md]: 'default',
    };

    return mapSize[switchSize];
  };

  // Function to handle the click event
  const handleClick = (): void => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  // Function to get [supportingText] color based on state
  const getSupportingTextColor = (): string => {
    let color: string;

    if (disabled) {
      color = theme.color.switch.supportingTextDisabledColor;
    } else if (isSupportingTextHovered) {
      color = theme.color.switch.supportingTextHoverColor;
    } else {
      color = theme.color.switch.supportingTextColor;
    }

    return color;
  };

  return (
    <Container
      size={size}
      data-testid={testId}
    >
      <div
        onMouseEnter={() => setIsSupportingTextHovered(true)}
        onMouseLeave={() => setIsSupportingTextHovered(false)}
      >
        <Element
          className={className}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          size={convertSizeToAntD(size)}
        />
      </div>
      {text && (
        <TextsContainer
          onMouseEnter={() => setIsSupportingTextHovered(true)}
          onMouseLeave={() => setIsSupportingTextHovered(false)}
        >
          <TitleContainer
            checked={checked}
            size={size}
            onClick={handleClick}
            disabled={disabled}
          >
            <Paragraph
              color={
                disabled
                  ? theme.color.switch.disabledTextColor
                  : theme.color.switch.textColor
              }
            >
              {text}
            </Paragraph>
          </TitleContainer>
          {supportingText && (
            <SupportingTextContainer
              checked={checked}
              size={size}
              onClick={handleClick}
              disabled={disabled}
            >
              <SupportingText size={size}>
                <Paragraph color={getSupportingTextColor()} size={ParagraphSize.sm}>
                  {supportingText}
                </Paragraph>
              </SupportingText>
            </SupportingTextContainer>
          )}
        </TextsContainer>
      )}
    </Container>
  );
}

export { Switch };
